import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSocket } from './SocketContext';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';
import { playEndTone, playRingback, playRingtone, stopCallTone, unlockCallAudio } from '../utils/callTones';
import { triggerHaptic } from '../utils/nativeBridge';
import {
  getCallAudioState, onCallAudioChange, setCallAudioDevice, startCallAudio, stopCallAudio,
} from '../utils/audioRoute';
import { optimizeAudioSdp, prioritizeAudioSender } from '../utils/callResilience';

const GroupCallContext = createContext(null);

const buildRtcConfig = () => {
  const iceServers = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
  ];
  if (process.env.REACT_APP_TURN_URL) {
    iceServers.push({
      urls: process.env.REACT_APP_TURN_URL.split(','),
      username: process.env.REACT_APP_TURN_USERNAME,
      credential: process.env.REACT_APP_TURN_CREDENTIAL,
    });
  }
  return {
    iceServers,
    iceCandidatePoolSize: 2,
    bundlePolicy: 'max-bundle',
    rtcpMuxPolicy: 'require',
  };
};

const AUDIO_CONSTRAINTS = {
  echoCancellation: { ideal: true },
  noiseSuppression: { ideal: true },
  autoGainControl: { ideal: true },
  echoCancellationType: { ideal: 'system' },
  googEchoCancellation: true,
  googExperimentalEchoCancellation: true,
  googAutoGainControl: true,
  googExperimentalAutoGainControl: true,
  googNoiseSuppression: true,
  googExperimentalNoiseSuppression: true,
  googHighpassFilter: true,
  googTypingNoiseDetection: true,
  googAudioMirroring: false,
};

const videoConstraints = (facing = 'user') => ({
  facingMode: facing,
  width: { ideal: 1280, max: 1280 },
  height: { ideal: 720, max: 720 },
  frameRate: { ideal: 24, max: 30 },
});

export const GroupCallProvider = ({ children }) => {
  const socket = useSocket();
  const { user } = useAuth();
  const toast = useToast();

  const [activeCall, setActiveCall] = useState(null); // active call metadata { callId, groupId, groupName, groupAvatar, video, startedAt, participants }
  const [incomingCall, setIncomingCall] = useState(null); // incoming call prompt { callId, groupId, groupName, groupAvatar, caller, video }
  const [isInGroupCall, setIsInGroupCall] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [cameraFacing, setCameraFacing] = useState('user');
  const [peers, setPeers] = useState({}); // socketId -> { socketId, userId, user, stream, isMuted, isCameraOff }
  const [activeCallsByGroup, setActiveCallsByGroup] = useState({}); // groupId -> activeCall
  const [audioRoute, setAudioRoute] = useState({ devices: [], current: null });

  const localStreamRef = useRef(null);
  const [localStream, setLocalStream] = useState(null);
  const peerConnectionsRef = useRef({}); // socketId -> RTCPeerConnection
  const activeCallRef = useRef(null);
  activeCallRef.current = activeCall;
  const isVideoEnabledRef = useRef(isVideoEnabled);
  isVideoEnabledRef.current = isVideoEnabled;
  const isMutedRef = useRef(isMuted);
  isMutedRef.current = isMuted;

  // Track Audio Route
  useEffect(() => {
    getCallAudioState().then(setAudioRoute);
    return onCallAudioChange(setAudioRoute);
  }, []);

  // Cleanup helper for all peer connections and local media
  const cleanupMediaAndPeers = useCallback(() => {
    stopCallTone();
    stopCallAudio();

    // Close all WebRTC peer connections
    Object.values(peerConnectionsRef.current).forEach((pc) => {
      try {
        pc.close();
      } catch (_) {}
    });
    peerConnectionsRef.current = {};

    // Stop local media tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => {
        try {
          t.stop();
        } catch (_) {}
      });
      localStreamRef.current = null;
    }

    setLocalStream(null);
    setPeers({});
    setIsInGroupCall(false);
    setIsMinimized(false);
    setActiveCall(null);
  }, []);

  // Helper to acquire local user media (audio + optional video)
  const acquireLocalMedia = useCallback(async (withVideo = true, facing = 'user') => {
    try {
      const constraints = {
        audio: AUDIO_CONSTRAINTS,
        video: withVideo ? videoConstraints(facing) : false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStreamRef.current = stream;
      setLocalStream(stream);
      setIsVideoEnabled(withVideo);
      setIsMuted(false);
      startCallAudio().then(setAudioRoute).catch(() => {});
      return stream;
    } catch (err) {
      console.warn('Could not acquire audio/video stream with video, trying audio only:', err);
      try {
        const audioOnly = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS });
        localStreamRef.current = audioOnly;
        setLocalStream(audioOnly);
        setIsVideoEnabled(false);
        setIsMuted(false);
        startCallAudio().then(setAudioRoute).catch(() => {});
        return audioOnly;
      } catch (audioErr) {
        console.error('Failed to acquire any media devices:', audioErr);
        toast.error('Could not access microphone or camera');
        return null;
      }
    }
  }, [toast]);

  // Create an RTCPeerConnection to an individual participant
  const createPeerConnection = useCallback((peerSocketId, peerUser, isInitiator) => {
    if (peerConnectionsRef.current[peerSocketId]) {
      try {
        peerConnectionsRef.current[peerSocketId].close();
      } catch (_) {}
    }

    const pc = new RTCPeerConnection(buildRtcConfig());
    peerConnectionsRef.current[peerSocketId] = pc;

    // Add local tracks to this connection
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    // Send ICE candidates to remote peer via socket relay
    pc.onicecandidate = (event) => {
      if (event.candidate && socket) {
        socket.emit('group-call:signal', {
          toSocketId: peerSocketId,
          signal: { candidate: event.candidate },
        });
      }
    };

    // Receive remote tracks
    pc.ontrack = (event) => {
      if (event.streams && event.streams[0]) {
        const remoteStream = event.streams[0];
        setPeers((prev) => ({
          ...prev,
          [peerSocketId]: {
            socketId: peerSocketId,
            userId: peerUser?.userId || peerUser?.id,
            user: peerUser,
            stream: remoteStream,
            isMuted: !!peerUser?.isMuted,
            isCameraOff: !!peerUser?.isCameraOff,
          },
        }));
      }
    };

    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === 'connected') {
        prioritizeAudioSender(pc);
      } else if (s === 'failed') {
        if (typeof pc.restartIce === 'function') {
          pc.restartIce();
        }
      }
    };

    // If initiator, generate offer
    if (isInitiator) {
      pc.onnegotiationneeded = async () => {
        try {
          const offer = await pc.createOffer();
          const localOffer = { ...offer, sdp: optimizeAudioSdp(offer.sdp) };
          await pc.setLocalDescription(localOffer);
          if (socket) {
            socket.emit('group-call:signal', {
              toSocketId: peerSocketId,
              signal: { sdp: pc.localDescription },
            });
          }
          prioritizeAudioSender(pc);
        } catch (e) {
          console.error('Group call offer error:', e);
        }
      };
    }

    return pc;
  }, [socket]);

  // ── 1. Start a Group Call ──────────────────────────────────────────
  const startGroupCall = useCallback(async (group, { video = true } = {}) => {
    if (!socket || !user || !group) return;
    unlockCallAudio();
    triggerHaptic('light');

    const groupId = group.groupId || group.id;
    const stream = await acquireLocalMedia(video, 'user');
    if (!stream) return;

    socket.emit('group-call:start', { groupId, video }, (res) => {
      if (res?.error) {
        toast.error(res.error === 'not_a_member' ? 'You are not in this group' : 'Failed to start call');
        cleanupMediaAndPeers();
        return;
      }

      setActiveCall(res.activeCall);
      setIsInGroupCall(true);
      playRingback();
      setTimeout(stopCallTone, 4000);
    });
  }, [socket, user, acquireLocalMedia, toast, cleanupMediaAndPeers]);

  // ── 2. Join an ongoing Group Call ──────────────────────────────────
  const joinGroupCall = useCallback(async (groupId, { video = true } = {}) => {
    if (!socket || !user) return;
    triggerHaptic('light');
    stopCallTone();

    const stream = await acquireLocalMedia(video, 'user');
    if (!stream) return;

    socket.emit('group-call:join', { groupId, video, isMuted: false }, (res) => {
      if (res?.error) {
        toast.error('Could not join group call');
        cleanupMediaAndPeers();
        return;
      }

      setActiveCall(res.activeCall);
      setIsInGroupCall(true);
      setIncomingCall(null);
    });
  }, [socket, user, acquireLocalMedia, toast, cleanupMediaAndPeers]);

  // ── 3. Accept incoming Group Call ──────────────────────────────────
  const acceptIncomingGroupCall = useCallback(async ({ video = true } = {}) => {
    if (!incomingCall) return;
    const gid = incomingCall.groupId;
    const withVid = video !== undefined ? video : incomingCall.video;
    setIncomingCall(null);
    stopCallTone();
    await joinGroupCall(gid, { video: withVid });
  }, [incomingCall, joinGroupCall]);

  // ── 4. Decline incoming Group Call ──────────────────────────────────
  const declineIncomingGroupCall = useCallback(() => {
    if (!incomingCall || !socket) return;
    triggerHaptic('light');
    stopCallTone();
    socket.emit('group-call:decline', { groupId: incomingCall.groupId });
    setIncomingCall(null);
  }, [incomingCall, socket]);

  // ── 5. Leave current Group Call ─────────────────────────────────────
  const leaveGroupCall = useCallback(() => {
    triggerHaptic('medium');
    playEndTone();
    if (socket && activeCallRef.current?.groupId) {
      socket.emit('group-call:leave', { groupId: activeCallRef.current.groupId });
    }
    cleanupMediaAndPeers();
  }, [socket, cleanupMediaAndPeers]);

  // ── 5b. Refresh active call status for a group ──────────────────────
  const refreshActiveCall = useCallback((groupId) => {
    if (!socket || !groupId) return;
    socket.emit('group-call:get-active', { groupId }, (res) => {
      if (res && 'activeCall' in res) {
        setActiveCallsByGroup((prev) => ({
          ...prev,
          [groupId]: res.activeCall,
        }));
      }
    });
  }, [socket]);

  // ── 6. Toggle Microphone Mute ───────────────────────────────────────
  const toggleGroupCallMute = useCallback(() => {
    triggerHaptic('selection');
    const next = !isMutedRef.current;
    setIsMuted(next);

    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach((t) => {
        t.enabled = !next;
      });
    }

    if (socket && activeCallRef.current?.groupId) {
      socket.emit('group-call:media-toggle', {
        groupId: activeCallRef.current.groupId,
        isMuted: next,
      });
    }
  }, [socket]);

  // ── 7. Toggle Camera On/Off ─────────────────────────────────────────
  const toggleGroupCallVideo = useCallback(async () => {
    triggerHaptic('selection');
    const next = !isVideoEnabledRef.current;
    setIsVideoEnabled(next);

    if (localStreamRef.current) {
      const vTrack = localStreamRef.current.getVideoTracks()[0];
      if (vTrack) {
        vTrack.enabled = next;
      } else if (next) {
        // Need to request camera track
        try {
          const fresh = await navigator.mediaDevices.getUserMedia({
            video: videoConstraints(cameraFacing),
          });
          const newVTrack = fresh.getVideoTracks()[0];
          if (newVTrack) {
            localStreamRef.current.addTrack(newVTrack);
            setLocalStream(new MediaStream(localStreamRef.current.getTracks()));
            // Add to peer connections
            Object.values(peerConnectionsRef.current).forEach((pc) => {
              pc.addTrack(newVTrack, localStreamRef.current);
            });
          }
        } catch (e) {
          console.error('Failed to enable camera track:', e);
        }
      }
    }

    if (socket && activeCallRef.current?.groupId) {
      socket.emit('group-call:media-toggle', {
        groupId: activeCallRef.current.groupId,
        isCameraOff: !next,
      });
    }
  }, [socket, cameraFacing]);

  // ── 8. Flip Camera (Front / Back) ───────────────────────────────────
  const flipGroupCallCamera = useCallback(async () => {
    triggerHaptic('selection');
    const nextFacing = cameraFacing === 'user' ? 'environment' : 'user';
    setCameraFacing(nextFacing);

    if (!localStreamRef.current) return;
    const oldTrack = localStreamRef.current.getVideoTracks()[0];
    if (oldTrack) {
      oldTrack.stop();
      localStreamRef.current.removeTrack(oldTrack);
    }

    try {
      const fresh = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints(nextFacing),
      });
      const newTrack = fresh.getVideoTracks()[0];
      if (newTrack) {
        localStreamRef.current.addTrack(newTrack);
        setLocalStream(new MediaStream(localStreamRef.current.getTracks()));

        // Replace track on existing peer connections
        Object.values(peerConnectionsRef.current).forEach((pc) => {
          const senders = pc.getSenders ? pc.getSenders() : [];
          const videoSender = senders.find((s) => s.track && s.track.kind === 'video');
          if (videoSender) {
            videoSender.replaceTrack(newTrack).catch(() => {});
          } else {
            pc.addTrack(newTrack, localStreamRef.current);
          }
        });
      }
    } catch (err) {
      console.error('flip camera error:', err);
    }
  }, [cameraFacing]);

  // Audio output device selection
  const selectAudioOutput = useCallback(async (deviceId) => {
    await setCallAudioDevice(deviceId);
  }, []);

  // ── Sockets Listeners for Group Calls ──────────────────────────────
  useEffect(() => {
    if (!socket || !user) return;

    // 1. Existing peers already in the call when we join
    const handleExistingPeers = (existingPeersList) => {
      (existingPeersList || []).forEach((peer) => {
        // We are the newcomer, so we initiate the offer to existing peers
        createPeerConnection(peer.socketId, peer, true);
        setPeers((prev) => ({
          ...prev,
          [peer.socketId]: {
            socketId: peer.socketId,
            userId: peer.userId,
            user: peer,
            stream: null,
            isMuted: !!peer.isMuted,
            isCameraOff: !!peer.isCameraOff,
          },
        }));
      });
    };

    // 2. A newcomer joins the room
    const handlePeerJoined = (newcomer) => {
      // Newcomer will initiate the offer, we prepare peer connection as receiver
      createPeerConnection(newcomer.socketId, newcomer, false);
      setPeers((prev) => ({
        ...prev,
        [newcomer.socketId]: {
          socketId: newcomer.socketId,
          userId: newcomer.userId,
          user: newcomer,
          stream: null,
          isMuted: !!newcomer.isMuted,
          isCameraOff: !!newcomer.isCameraOff,
        },
      }));
      toast.info(`${newcomer.username || 'A member'} joined the call`);
    };

    // 3. WebRTC Signal Relay (SDP offer / answer / ICE candidate)
    const handleSignal = async ({ fromSocketId, fromUserId, signal }) => {
      const pc = peerConnectionsRef.current[fromSocketId];
      if (!pc || !signal) return;

      try {
        if (signal.sdp) {
          const remoteSdp = { ...signal.sdp, sdp: optimizeAudioSdp(signal.sdp.sdp) };
          await pc.setRemoteDescription(new RTCSessionDescription(remoteSdp));
          if (signal.sdp.type === 'offer') {
            const answer = await pc.createAnswer();
            const localAnswer = { ...answer, sdp: optimizeAudioSdp(answer.sdp) };
            await pc.setLocalDescription(localAnswer);
            socket.emit('group-call:signal', {
              toSocketId: fromSocketId,
              signal: { sdp: pc.localDescription },
            });
            prioritizeAudioSender(pc);
          }
        } else if (signal.candidate) {
          await pc.addIceCandidate(new RTCIceCandidate(signal.candidate));
        }
      } catch (err) {
        console.error('Error handling WebRTC signal:', err);
      }
    };

    // 4. Remote peer media toggle (mute / camera)
    const handlePeerMediaToggle = ({ socketId, isMuted: m, isCameraOff: c }) => {
      setPeers((prev) => {
        const target = prev[socketId];
        if (!target) return prev;
        return {
          ...prev,
          [socketId]: {
            ...target,
            ...(typeof m === 'boolean' ? { isMuted: m } : {}),
            ...(typeof c === 'boolean' ? { isCameraOff: c } : {}),
          },
        };
      });
    };

    // 5. Remote peer left
    const handlePeerLeft = ({ socketId, userId: leftUserId }) => {
      if (peerConnectionsRef.current[socketId]) {
        try {
          peerConnectionsRef.current[socketId].close();
        } catch (_) {}
        delete peerConnectionsRef.current[socketId];
      }
      setPeers((prev) => {
        const next = { ...prev };
        delete next[socketId];
        return next;
      });
    };

    // 6. Incoming group call alert
    const handleIncoming = (callData) => {
      // Check Do Not Disturb (DND) or muted threads
      let isDnd = false;
      try {
        isDnd = localStorage.getItem('bazzef_dnd') === 'true';
      } catch (_) {}

      let isMutedThread = false;
      try {
        const mutedThreads = JSON.parse(localStorage.getItem('bazzef_muted_threads') || '{}');
        isMutedThread = !!mutedThreads[`group_${callData.groupId}`];
      } catch (_) {}

      // If user is in DND or muted thread, do not ring or pop full-screen modal
      if (isDnd || isMutedThread) {
        toast.info(`🎥 Live call in "${callData.groupName}" — tap to join`, {
          action: {
            label: 'Join',
            onClick: () => joinGroupCall(callData.groupId, { video: callData.video }),
          },
        });
        return;
      }

      setIncomingCall(callData);
      playRingtone();
    };

    // 7. Call ended by server (e.g. all participants left)
    const handleCallEnded = ({ groupId }) => {
      if (activeCallRef.current?.groupId === groupId) {
        toast.info('Group call ended');
        cleanupMediaAndPeers();
      }
    };

    // 8. Active call status change in a group
    const handleActiveChanged = ({ groupId, activeCall: callInfo }) => {
      setActiveCallsByGroup((prev) => ({
        ...prev,
        [groupId]: callInfo,
      }));

      // If we are in this call, update activeCall metadata
      if (activeCallRef.current?.groupId === groupId && callInfo) {
        setActiveCall((prev) => ({
          ...prev,
          ...callInfo,
        }));
      }
    };

    socket.on('group-call:existing-peers', handleExistingPeers);
    socket.on('group-call:peer-joined', handlePeerJoined);
    socket.on('group-call:signal', handleSignal);
    socket.on('group-call:peer-media-toggle', handlePeerMediaToggle);
    socket.on('group-call:peer-left', handlePeerLeft);
    socket.on('group-call:incoming', handleIncoming);
    socket.on('group-call:ended', handleCallEnded);
    socket.on('group-call:active-changed', handleActiveChanged);

    return () => {
      socket.off('group-call:existing-peers', handleExistingPeers);
      socket.off('group-call:peer-joined', handlePeerJoined);
      socket.off('group-call:signal', handleSignal);
      socket.off('group-call:peer-media-toggle', handlePeerMediaToggle);
      socket.off('group-call:peer-left', handlePeerLeft);
      socket.off('group-call:incoming', handleIncoming);
      socket.off('group-call:ended', handleCallEnded);
      socket.off('group-call:active-changed', handleActiveChanged);
    };
  }, [socket, user, createPeerConnection, toast, cleanupMediaAndPeers, joinGroupCall]);

  const value = {
    activeCall,
    incomingCall,
    isInGroupCall,
    isMinimized,
    isMuted,
    isVideoEnabled,
    cameraFacing,
    localStream,
    peers,
    audioRoute,
    activeCallsByGroup,
    startGroupCall,
    joinGroupCall,
    leaveGroupCall,
    refreshActiveCall,
    acceptIncomingGroupCall,
    declineIncomingGroupCall,
    toggleGroupCallMute,
    toggleGroupCallVideo,
    flipGroupCallCamera,
    setIsMinimized,
    selectAudioOutput,
  };

  return <GroupCallContext.Provider value={value}>{children}</GroupCallContext.Provider>;
};

export const useGroupCall = () => {
  const ctx = useContext(GroupCallContext);
  if (!ctx) throw new Error('useGroupCall must be used within GroupCallProvider');
  return ctx;
};
